
import { useCallback } from 'react';
import type { PermissionsState } from '@/types/permissions';
import { 
  getCachedPermissions, 
  setCachedPermissions, 
  wasRecentlyInvalidated 
} from '@/services/permissions';
import { subscriptionUtils } from '../utils/subscriptionUtils';

/**
 * Hook for handling permissions cache operations
 */
export const useCacheHandling = (
  userId: string | undefined,
  setPermissions: (permissions: PermissionsState) => void,
  setSubscriptionSource: (source: 'stripe' | 'admin_override' | 'fallback' | undefined) => void,
  hasInitiallyRefreshedRef: React.MutableRefObject<boolean>
) => {
  const tryGetCachedPermissions = useCallback((refreshPermissionsCallback?: () => Promise<void>): boolean => {
    if (!userId) return false;

    const recentlyInvalidated = wasRecentlyInvalidated(userId);
    if (recentlyInvalidated) return false;

    const cachedPermissions = getCachedPermissions(userId);
    if (!cachedPermissions) {
      // Cache miss - trigger lazy refresh if callback provided
      if (refreshPermissionsCallback) {
        console.log('🔄 Cache miss detected, triggering lazy refresh for user:', userId);
        // Trigger refresh asynchronously to avoid blocking
        setTimeout(() => {
          refreshPermissionsCallback();
        }, 100);
      }
      return false;
    }

    const validatedPermissions = subscriptionUtils.checkSubscriptionExpiry(cachedPermissions);
    setPermissions(validatedPermissions);
    setSubscriptionSource(validatedPermissions.source as 'stripe' | 'admin_override' | 'fallback');

    // Mark as initially refreshed if this is the first time
    if (!hasInitiallyRefreshedRef.current) {
      hasInitiallyRefreshedRef.current = true;
    }

    return true;
  }, [userId, setPermissions, setSubscriptionSource, hasInitiallyRefreshedRef]);

  const cachePermissions = useCallback((
    permissions: PermissionsState,
    options?: {
      customTTL?: number;
      version?: string;
      source?: 'api' | 'cache' | 'fallback';
      eventTimestamp?: number;
      invalidationReason?: string;
    }
  ) => {
    if (!userId) return;
    
    setCachedPermissions(userId, permissions, options);
  }, [userId]);

  return {
    tryGetCachedPermissions,
    cachePermissions,
  };
};
